import { useEffect, useRef, useState } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, ChevronDown, Instagram, LogIn, LogOut, Mail, Menu, MessageCircle, Sparkles, User, Video, X } from "lucide-react";
import "@/App.css";
import QuoteModal from "./components/QuoteModal";
import ServiceModal from "./components/ServiceModal";
import ChatPanel from "./components/ChatPanel";
import AuthCallback from "./components/AuthCallback";
import Benefits from "./components/Benefits";
import OrderModal from "./components/OrderModal";
import { services, WHATSAPP_URL } from "./lib/services";
import { brl } from "./lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const MONTHLY = new Set(["beta", "plus"]);

const LogoMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="none" stroke="rgba(245,168,11,.35)" />
    <g fill="none" stroke="#f5a80b" strokeWidth="3" strokeLinecap="round">
      <circle cx="32" cy="32" r="19" />
      <path d="M51 32 L38.9 39" /><path d="M41.5 48.5 L32.4 39.3" /><path d="M22.5 48.5 L28 36.6" />
      <path d="M13 32 L25.1 25" /><path d="M22.5 15.5 L31.6 24.7" /><path d="M41.5 15.5 L36 27.4" />
    </g>
  </svg>
);

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="h-mask">
    <motion.span initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 1.05, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, className, delay = 0 }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 44 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, ease: EASE, delay }}>
    {children}
  </motion.div>
);

function ServicePrice({ service, pricing }) {
  const pr = pricing?.services?.[service.id];
  const suffix = MONTHLY.has(service.id) ? "/mês" : "";
  if (pr && pr.discount > 0) {
    return (
      <div className="service-price has-discount" data-testid={`service-price-${service.id}`}>
        <span><s>{brl(pr.base)}{suffix}</s> <strong>{brl(pr.final)}{suffix}</strong></span>
        <span className="price-save">Você economiza {brl(pr.discount)}</span>
      </div>
    );
  }
  return <div className="service-price">{service.price}</div>;
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [serviceModal, setServiceModal] = useState(null);
  const [order, setOrder] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [benefits, setBenefits] = useState(null);
  const [pricing, setPricing] = useState(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const refreshPricing = () =>
    fetch(`${API}/pricing`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setPricing(data))
      .catch(() => {});

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) { setUser(data.user); setBenefits(data.benefits); } })
      .catch(() => {})
      .finally(refreshPricing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setUser(null);
    setBenefits(null);
    refreshPricing();
  };

  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) lenisRef.current ? lenisRef.current.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const openQuote = (prefill = "") => { setServiceModal(null); setOrder(null); setQuote({ prefill }); };
  const openOrder = (service) => { setServiceModal(null); setQuote(null); setOrder({ service }); };

  const onOrdered = () => {
    refreshPricing();
    if (user) {
      fetch(`${API}/benefits`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => data && setBenefits(data))
        .catch(() => {});
    }
  };

  return (
    <div className="site-shell">
      <div className="grain" aria-hidden="true" />
      <nav className="nav" data-testid="site-navigation">
        <button className="brand" onClick={() => jump("top")} data-testid="brand-home">
          <LogoMark />
          <span className="brand-word">EDITVIEIRA<sup>®</sup></span>
          <span className="brand-flash" aria-hidden="true"><Video size={14} /><i className="flash-dot" /></span>
        </button>
        <div className={`nav-links ${menuOpen ? "is-open" : ""}`}>
          <button onClick={() => jump("servicos")} data-testid="nav-services-link">Serviços</button>
          <button onClick={() => jump("sobre")} data-testid="nav-about-link">Sobre mim</button>
          <button onClick={() => jump("contato")} data-testid="nav-contact-link">Contato</button>
        </div>
        <div className="nav-side">
          {user ? (
            <div className="user-chip" data-testid="user-chip">
              {user.picture ? <img src={user.picture} alt="" /> : <User size={13} />}
              <span>{user.name.split(" ")[0]}</span>
              <button onClick={logout} data-testid="logout-button" aria-label="Sair"><LogOut size={13} /></button>
            </div>
          ) : (
            <button className="login-btn" onClick={startLogin} data-testid="login-button">
              <LogIn size={13} /> <span>Entrar / Cadastrar</span>
            </button>
          )}
          <button className="nav-cta" onClick={() => openQuote()} data-testid="nav-quote-button">Solicitar orçamento <ArrowUpRight size={15} /></button>
        </div>
        <button className="mobile-menu" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-button" aria-label="Abrir menu">{menuOpen ? <X /> : <Menu />}</button>
      </nav>

      <main id="top">
        <section className="hero section-pad">
          <div className="hero-copy">
            <motion.div className="eyebrow" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: EASE }}>
              <i /> RIO DE JANEIRO · ZONA OESTE
            </motion.div>
            <h1 data-testid="hero-headline">
              <MaskedLine delay={0.15}>Imagem que</MaskedLine>
              <MaskedLine delay={0.27}><em>conta.</em> Impacto</MaskedLine>
              <MaskedLine delay={0.39}>que fica.</MaskedLine>
            </h1>
            <motion.p className="hero-intro" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}>
              Edição, direção e storytelling para transformar ideias em conteúdo que conecta pessoas e movimenta marcas.
            </motion.p>
            <motion.div className="hero-actions" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.74 }}>
              <button className="button button-primary" onClick={() => openQuote()} data-testid="hero-quote-button">Vamos criar <ArrowUpRight size={18} /></button>
              <button className="text-button" onClick={() => jump("servicos")} data-testid="hero-services-button">Explorar serviços <ChevronDown size={16} /></button>
            </motion.div>
          </div>
        </section>

        <section className="intro section-pad" data-testid="intro-section">
          <Reveal>
            <div className="eyebrow"><i /> APRESENTAÇÃO</div>
            <p className="intro-lead">
              Filmmaker, storymaker e editor de vídeos. Minha evolução no audiovisual veio da prática: transformar ideias e material bruto em conteúdo profissional, visualmente atraente e pensado para cada plataforma digital — <em>com criatividade, método e estratégia.</em>
            </p>
          </Reveal>
          <div className="intro-cols">
            <Reveal delay={0.12}>
              <p>Atuo com diferentes estilos e formatos — Reels, Stories, vídeos institucionais, promocionais e conteúdo para redes sociais. Cortes, legendas, transições, ajustes de ritmo e tratamento visual entram em cada projeto como ferramentas de comunicação, nunca como enfeite.</p>
            </Reveal>
            <Reveal delay={0.22}>
              <p>Uma boa edição organiza a mensagem, fortalece a identidade visual e amplia o impacto do conteúdo. É o que separa uma gravação simples de um vídeo dinâmico, profissional e estratégico. Cada projeto é adaptado às necessidades, à identidade e aos objetivos de cada cliente — com qualidade, organização, agilidade e atenção aos detalhes do início ao fim.</p>
            </Reveal>
          </div>
        </section>

        {user && benefits && <Benefits user={user} benefits={benefits} onOrder={openOrder} />}

        <section className="marquee" aria-label="Áreas de atuação">
          <div className="marquee-track">
            {[0, 1].map((n) => (
              <span className="marquee-chunk" key={n} aria-hidden={n === 1}>
                EDIÇÃO DE VÍDEO <i>✦</i> FILMMAKING <i>✦</i> STORYTELLING <i>✦</i> CAPAS DE REELS <i>✦</i> CONTEÚDO QUE CONECTA <i>✦</i>
              </span>
            ))}
          </div>
        </section>

        <section className="services section-pad" id="servicos">
          <Reveal className="section-heading">
            <div>
              <div className="eyebrow"><i /> O QUE EU FAÇO</div>
              <h2>Serviços que dão<br /><em>vida</em> à sua história.</h2>
            </div>
            <p>Do primeiro corte à capa que prende o olhar: cada formato foi pensado para um momento do seu conteúdo.</p>
          </Reveal>
          <div className="service-grid">
            {services.map((service, index) => {
              const Icon = service.icon;
              const pr = pricing?.services?.[service.id];
              return (
                <Reveal key={service.id} delay={index * 0.1}>
                  <article className={`service-card card-${index + 1}`} data-testid={`service-card-${index + 1}`}>
                    <div className="card-top"><span>0{index + 1}</span><Icon size={22} /></div>
                    <h3>{service.name}</h3>
                    <p className="service-tag">{service.tag}</p>
                    <ul className="adv-list">
                      {service.advantages.map((adv) => <li key={adv}><Check size={13} /> {adv}</li>)}
                    </ul>
                    {pr?.benefit && <p className="card-benefit" data-testid={`card-benefit-${service.id}`}>1 semana grátis · seu benefício de boas-vindas</p>}
                    <ServicePrice service={service} pricing={pricing} />
                    <div className="card-actions">
                      <button className="btn-ghost" onClick={() => setServiceModal(service)} data-testid={`service-more-button-${index + 1}`}>Saber mais</button>
                      <button className="card-link" onClick={() => openOrder(service)} data-testid={`service-quote-button-${index + 1}`}>Quero este <span>→</span></button>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </section>

        <section className="about section-pad" id="sobre">
          <Reveal className="about-photo-wrap">
            <div className="about-photo" data-testid="about-photo">
              <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_vieira-studio/artifacts/767c3dd3c147ad36_8B9DD0A8-16A5-436D-B66B-D8854A4C8F43.png"
                alt="Vieira com sua câmera de filmmaker" data-testid="biography-portrait" />
            </div>
          </Reveal>
          <Reveal className="about-copy" delay={0.15}>
            <div className="eyebrow"><i /> SOBRE VIEIRA</div>
            <h2>Por trás de cada<br /><em>frame,</em> uma intenção.</h2>
            <p>Opa! Eu sou o Vieira. Sou editor de vídeos, filmmaker e storymaker, além de Bombeiro Profissional Civil. Minha relação com o audiovisual nasceu do desejo de transformar ideias em imagens com propósito, identidade e verdade.</p>
            <p>Mais do que editar vídeos, busco entender o que precisa ser comunicado, para quem e como essa mensagem pode ser sentida. Cada corte, ritmo, transição e escolha visual faz parte de uma construção.</p>
            <div className="about-signature">“Porque uma boa imagem pode chamar atenção. Mas uma história bem construída permanece.” <span>— Vieira</span></div>
          </Reveal>
        </section>

        <section className="contact-band section-pad" id="contato">
          <Reveal>
            <div className="eyebrow"><i /> VAMOS CONVERSAR</div>
            <h2>Tem uma ideia?<br /><em>Eu dou movimento.</em></h2>
          </Reveal>
          <Reveal delay={0.15}>
            <button className="button button-primary" onClick={() => openQuote()} data-testid="contact-quote-button">Solicitar orçamento <ArrowUpRight size={18} /></button>
          </Reveal>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-brand"><span>EDITVIEIRA®</span><p>Imagem que conta. Impacto que fica.</p></div>
        <div className="footer-contact">
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link"><MessageCircle size={16} /> WhatsApp</a>
          <a href="mailto:cleurvieira@gmail.com" data-testid="footer-email-link"><Mail size={16} /> E-mail</a>
          <a href="https://instagram.com/cvrvieira" target="_blank" rel="noreferrer" data-testid="footer-instagram-link"><Instagram size={16} /> @cvrvieira</a>
        </div>
        <div className="footer-note">© 2023 EDITVIEIRA · Rio de Janeiro</div>
      </footer>

      <motion.button className="chat-fab" onClick={() => setChatOpen(true)} data-testid="open-ai-chat-button"
        whileHover={{ y: -4 }} whileTap={{ scale: 0.96 }}>
        <Sparkles size={18} /><span>EDITVIEIRA AI</span>
      </motion.button>

      <AnimatePresence>
        {quote && <QuoteModal key="quote" prefill={quote.prefill} close={() => setQuote(null)} />}
        {serviceModal && <ServiceModal key={serviceModal.id} service={serviceModal} close={() => setServiceModal(null)} onQuote={openOrder} />}
        {order && <OrderModal key={order.service.id} service={order.service} pricing={pricing} user={user} close={() => setOrder(null)} onOrdered={onOrdered} />}
        {chatOpen && <ChatPanel key="chat" close={() => setChatOpen(false)} />}
      </AnimatePresence>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <App />;
}

export default function Root() { return <BrowserRouter><AppRouter /></BrowserRouter>; }
